import { inject } from '@angular/core';
import { Router, type CanMatchFn } from '@angular/router';
import { map } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { trackingPathFor } from '../auth/landing';

/**
 * `/tracking/:token` is the path the printed QR code encodes (see
 * FRONTEND_TRACKING_BASE_URL in cftw-be) — a phone's native camera app opens
 * it directly, with no in-app scanning involved. This guard never actually
 * mounts a component here; it always resolves to a role-appropriate UrlTree:
 * the Designer/Admin tracking route for that token, `/forbidden` for a Judge
 * (idea_doc.md Step 3: Judges have no logistics reason to land on a tracking
 * dashboard), or `/login` with a `redirectTo` back to this same URL.
 *
 * Structured like roleGuard: an immediate answer when the user is already
 * authenticated, and a bootstrap() wait (single-flight, shared with any other
 * concurrent guard) when a token exists but the user hasn't loaded yet.
 */
export const trackingDispatchGuard: CanMatchFn = (_route, segments) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  // Route params aren't resolved during canMatch — read the token from the
  // matched segments instead. segments[0] is 'tracking', segments[1] is the token.
  const token = segments[1]?.path;
  if (!token) {
    return router.createUrlTree(['/']);
  }

  const dispatch = () => {
    // A seeded/temp password must be rotated before anything else — including a physical QR
    // scan, which is otherwise the one path that bypasses the shell's mustChangePasswordGuard
    // entirely (docs/adr/0014-*.md). The deep link to this token is lost; the garment tag can be
    // rescanned once the password is set.
    if (auth.mustChangePassword()) {
      return router.createUrlTree(['/set-password']);
    }
    const target = trackingPathFor(auth.role(), token);
    return target ? router.createUrlTree(target) : router.createUrlTree(['/forbidden']);
  };

  if (auth.isAuthenticated()) {
    return dispatch();
  }

  if (auth.hasAccessToken()) {
    return auth
      .bootstrap()
      .pipe(map((user) => (user ? dispatch() : router.createUrlTree(['/login']))));
  }

  return router.createUrlTree(['/login'], {
    queryParams: { redirectTo: `/tracking/${token}` },
  });
};
